import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { compareCases } from "@/mocks/site";

const steps = [
  { icon: "ri-focus-3-line", title: "选取同一话", desc: "锁定同一部作品的同一章节，确保对比口径一致。" },
  { icon: "ri-file-copy-2-line", title: "并列截图", desc: "把不同版本并排截图，逐格标出删页与译法差异。" },
  { icon: "ri-scales-3-line", title: "多维评分", desc: "从准确度、润色、嵌字、删页四个维度给出结论。" },
  { icon: "ri-flag-2-line", title: "给出结论", desc: "标注推荐版本，并说明差异对阅读体验的实际影响。" },
];

export default function Compare() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-32 pb-16">
        <nav aria-label="面包屑" className="text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
            <li><i className="ri-arrow-right-s-line" aria-hidden="true"></i></li>
            <li aria-current="page" className="text-foreground-800">翻译对比</li>
          </ol>
        </nav>

        <header className="mt-6 max-w-3xl">
          <p className="font-label text-sm text-primary-600">TRANSLATION COMPARISON</p>
          <h1 className="mt-2 font-heading text-3xl font-black text-foreground-950 md:text-5xl">翻译对比</h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            同一部作品的不同版本，翻译与嵌字差异到底有多大？我们把关键章节并排截图对比，
            删页差异、译法优劣、嵌字粗糙度一一标出，让你看明白该选哪一版、又到底少看了什么。
          </p>
        </header>

        {/* 方法步骤 */}
        <section className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} className="rounded-xl bg-background-100 p-5">
              <div className="flex items-center justify-between">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-500 text-lg text-background-50">
                  <i className={s.icon} aria-hidden="true"></i>
                </span>
                <span className="font-label text-2xl text-background-300">0{i + 1}</span>
              </div>
              <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">{s.title}</h2>
              <p className="mt-1.5 text-sm text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </section>

        {/* 对比案例 */}
        <section className="mt-12 flex flex-col gap-6">
          {compareCases.map((c) => (
            <article
              key={c.id}
              className="grid grid-cols-1 gap-5 overflow-hidden rounded-xl bg-background-50 ring-1 ring-background-200 p-4 transition-shadow hover:shadow-lift md:grid-cols-[320px_1fr] md:gap-6 md:p-6"
            >
              <div className="h-48 w-full overflow-hidden rounded-lg md:h-full md:min-h-[220px]">
                <img
                  src={c.image}
                  alt={`${c.title} 翻译对比图`}
                  title={`${c.title} 版本对比`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">{c.title}</h2>
                  <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                    结论：{c.result}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.editions.map((e) => (
                    <span key={e} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                      {e}
                    </span>
                  ))}
                </div>
                <ul className="mt-4 flex flex-col gap-2.5">
                  {c.points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                      <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-600" aria-hidden="true"></i>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </section>

        {/* 引导 */}
        <section className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Link
            to="/collections"
            className="group rounded-xl bg-background-100 p-6 transition-colors hover:bg-secondary-100"
          >
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              看完对比，想看推荐的润色版？
            </h2>
            <p className="mt-1 text-sm text-foreground-600">去神翻译合集，收录的都是逐话对比后的佳作。</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
              进入神翻译合集
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </span>
          </Link>
          <Link
            to="/avoid"
            className="group rounded-xl bg-background-100 p-6 transition-colors hover:bg-secondary-100"
          >
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              或者，先把踩过的坑记下来？
            </h2>
            <p className="mt-1 text-sm text-foreground-600">避雷区汇总了机翻、删页、嵌字粗糙的版本。</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
              进入避雷区
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" aria-hidden="true"></i>
            </span>
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}